/**
 * Beta access gate (frontend).
 *
 * - Reads `?beta=<code>` from the URL on mount.
 * - Validates against `/api/beta/check`. If valid, persists to localStorage
 *   under `rwo_beta_code` so the user doesn't have to keep the magic link.
 * - Exposes a hook `useBetaAccess()` which tells UI whether to render the
 *   real Login/Signup forms or the `<ComingSoon />` placeholder.
 *
 * The same code is also stamped on every `api` request via the
 * `X-Beta-Access` header — see `lib/api.js`.
 */
import { useEffect, useState } from "react";
import { api } from "./api";

const STORAGE_KEY = "rwo_beta_code";

export function getBetaCode() {
    try {
        return window.localStorage.getItem(STORAGE_KEY) || "";
    } catch {
        return "";
    }
}

export function setBetaCode(code) {
    try {
        if (code) window.localStorage.setItem(STORAGE_KEY, code);
        else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        // noop
    }
}

/**
 * Reads the URL on mount and, if `?beta=<code>` is present, validates and
 * stores it. Cleans the query param so the magic link is one-shot.
 *
 * Returns `{ ready, hasAccess, gateEnabled }`.
 *   - ready:        validation finished
 *   - hasAccess:    the stored/URL code passed validation
 *   - gateEnabled:  the backend is enforcing the gate (BETA_ACCESS_CODE set)
 */
export function useBetaAccess() {
    const [state, setState] = useState({ ready: false, hasAccess: false, gateEnabled: true });

    useEffect(() => {
        let cancel = false;
        const url = new URL(window.location.href);
        const fromQuery = (url.searchParams.get("beta") || "").trim();
        const stored = getBetaCode();
        const candidate = fromQuery || stored;

        // If a fresh code came in via the URL, persist it before validating —
        // that way a refresh during the in-flight request still works.
        if (fromQuery && fromQuery !== stored) setBetaCode(fromQuery);

        api
            .get("/beta/check", { params: candidate ? { code: candidate } : {} })
            .then(({ data }) => {
                if (cancel) return;
                if (!data.enabled) {
                    setState({ ready: true, hasAccess: true, gateEnabled: false });
                    return;
                }
                setState({ ready: true, hasAccess: !!data.valid, gateEnabled: true });
                if (!data.valid) setBetaCode("");
                // Strip ?beta=… from the address bar without a reload.
                if (fromQuery) {
                    url.searchParams.delete("beta");
                    window.history.replaceState({}, "", url.toString());
                }
            })
            .catch(() => {
                if (!cancel) setState({ ready: true, hasAccess: false, gateEnabled: true });
            });
        return () => { cancel = true; };
    }, []);

    return state;
}
